import React, { useMemo } from 'react';
import { Classable, HasChildren } from '@shapeable/types';
import styled, { css } from 'styled-components';
import { breakpoints, theme } from '@shapeable/theme';
import { PageContextDefinition, LayoutShell, SiteFooterLayout, UiConfig, UiProvider, useReady, useSpaces, EntityExplorerLayout, useOrganisations, useImpactStories, EntityBanner, useAuth, ComponentSwapProvider, UserMenuProvider, useActiveEntity, useModal, IndicatorLayoutsProvider } from '@shapeable/ui';
import * as pageLayouts from './page-layouts';
import * as sliceLayouts from './slice-layouts';
import { LANG_STRINGS } from '../lang';
import { DOING_CHALK, DOING_DEEP_BLUE, DOING_SIGNAL_BLUE, LayoutStyles, PALE_ORANGE, PRIMARY, PRIMARY_LIGHT, SOFT_BLUE, themeOverrides } from '../theme';
import { PagesProvider } from '../gatsby/providers/pages-provider';
import { EntityMapPanel, MapConfig, MapProvider, MapState } from '@shapeable/maps';
import { SidebarButtons } from './elements/sidebar-buttons';
import { SpacesProvider } from '../gatsby/providers/spaces-provider';
import * as EntityPageQueries from '../graphql/details';
import { ImpactStoriesProvider } from '../gatsby/providers/impact-stories-provider';
import { OrganisationsProvider } from '../gatsby/providers/organisations-provider';
import { PeopleProvider } from '../gatsby/providers/people-provider';
import { SpaceCard } from './entities/space-card';
import { OrganisationCard } from './entities/organisation-card';
import { PersonCard } from './entities/person-card';
import { DocumentsProvider } from '../gatsby/providers/documents-provider';
import { DocumentCard } from './entities/document-card';
import { Document } from '@shapeable/doing-co-types';
import { PostsProvider } from '../gatsby/providers/posts-provider';
import { ImpactStoryCard } from './entities/impact-story-card';
import { ChallengesProvider } from '../gatsby/providers/challenges-provider';
import { includes } from 'lodash';
import { lighten } from 'polished';
// import { menuItemIsAvailable } from '../utils/menu-item-is-available';
import { EventsProvider } from '../gatsby/providers/events-provider';
import { LinkEntityButton } from './elements/link-entity-button';
import { LinkCard } from './entities/link-card';
import { TeamWorkSpeakerIconGlyph } from './icons/team-work-speaker-icon';
import { FeedEntriesProvider } from '../gatsby/providers/feed-entries-provider';
import { useHowToVideos } from '../hooks/use-how-to-videos';
import { useHowToPosts } from '../hooks/use-how-to-posts';
import { DcoHomeIconGlyph } from './icons/dco-home-icon';
import { DcoPersonIconGlyph } from './icons/dco-person-icon';
import { DcoWorldIconGlyph } from './icons/dco-world-icon';
import { DcoDoersIconGlyph } from './icons/dco-doers-icon';
import { DcoDoingsIconGlyph } from './icons/dco-doings-icon';
import { DcoTownTeamsIcon, DcoTownTeamsIconGlyph } from './icons/dco-town-teams-icon';
import { DcoSearchIconGlyph } from './icons/dco-search-icon';
import { DcoPlaceIcon, DcoPlaceIconGlyph } from './icons/dco-place-icon';
import { DcoPositiveIconGlyph } from './icons/dco-positive-icon';
import { DcoSystemShaperIconGlyph } from './icons/dco-system-shaper-icon';
import { DcoOrganisationsIconGlyph } from './icons/dco-organisation-icon';
import { DcoQuickWinIconGlyph } from './icons/dco-quick-win-icon';
import { DcoDownloadIconGlyph } from './icons/dco-download-icon';
import { DcoLoginIconGlyph } from './icons/dco-login-icon';
import { DcoLogoutIconGlyph } from './icons/dco-logout-icon';
import { DcoNewsIconGlyph } from './icons/dco-news-icon';
import { DcoPlacemakingIcon } from './icons/dco-placemaking-icon';
import { isMailchimpModalSlug, showMailchimpModal } from './elements/mailchimp-modal';
import { IndicatorLayoutLinkButton } from './indicator-layouts';
import { LoggedInUserMenuItems } from './elements/logged-in-user-menu-items';
import { LoggedOutUserMenuItems } from './elements/logged-out-user-menu-items';
import { MapDeepLinkHandler } from './elements/map-deep-link-handler';

const initialMapState: MapState = {
  isVisible: false,
  isInitialized: false,
  zoom: 3.5,
  entityTypeNames: ['Space', 'Organisation', 'ImpactStory'],
  latitude: -25.441022303718128,
  longitude: 135.59765625000352,
};

const SIDEBAR_WIDTH = 60;

export type LayoutPropTypes = Classable & HasChildren & {
  path?: string;
  data?: any;
  pageContext?: PageContextDefinition;
};

const LayoutDefaultProps: LayoutPropTypes = {
};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    ${LayoutStyles};
  `,
});

const FooterStyles = breakpoints({
  base: css`
  `,
});

const BodyStyles = breakpoints({
  base: css`
    flex-grow: 1;
  `,
});

const MapStyles = breakpoints({
  base: css`
    
  `,
});

const TeamIconStyles = breakpoints({
  base: css`
    fill: ${theme.COLOR('canvas')};
    background-color: rgba(0, 0, 0, 0.40);
    border-radius: 50%;
  `,
});

// -------- Components -------->

const My = {
  Container: styled(LayoutShell)`${ContainerStyles}`,
  Body: styled.div`${BodyStyles}`,
  Footer: styled(SiteFooterLayout)`${FooterStyles}`,
  Map: styled(EntityMapPanel)`${MapStyles}`,
  SpaceMapIcon: styled(DcoTownTeamsIcon)`${TeamIconStyles}`,
};

export const Layout: React.FC<LayoutPropTypes> = (props) => {
  const { className, children } = props;

  const { isReady } = useReady();
  const entity = useActiveEntity();
  const modal = useModal();
  const auth = useAuth();

  const { isAuthenticated } = auth;

  const isExplorer = includes(['Topic', 'Trend', 'Challenge', 'Person', 'Organisation', 'Space', 'ImpactStory'], entity?.entityTypeName) || entity?.entityPath === '/movement';
  const isSigningIn = entity?.entityPath === '/signing-in';

  const mapConfig: MapConfig = {
    initialMapState,
    cluster: {
      enabled: true,
      radius: 48,
      maxZoom: 12,
    },
    applyEntityTypeFilters: () => true,
    entityTypeMapMarkers: {
      Space: My.SpaceMapIcon,
    },
    availableEntityTypeNames: ['Space', 'Organisation', 'ImpactStory'],
    items: () => {
      const organisations = useOrganisations();
      const doings = useImpactStories();
      const spaces = useSpaces();

      return [...spaces, ...organisations, ...doings];
    }
  };

  const onHeaderButtonClick = (slug: string) => {
    if (isMailchimpModalSlug(slug) && !isAuthenticated) {
      showMailchimpModal(modal);
    }
  }

  const UiConfig: UiConfig = {
    layouts: { pageLayouts, sliceLayouts, defaultPageLayout: pageLayouts.PageLayoutDefault },
    langStrings: LANG_STRINGS,
    theme: themeOverrides,
    header: {
      variant: 'default',
      boundary: 'content',
      showThemeMode: false,
      // headerButtonIsAvailable: () => isAuthenticated,
      // menuItemIsAvailable: menuItemIsAvailable(auth),
      showActiveBar: true,
      onHeaderButtonClick,
    },
    sidebar: {
      isAvailable: () => !isSigningIn,
      showFullscreenButton: true,
      color: PRIMARY, 
      buttons: <SidebarButtons />,
      width: SIDEBAR_WIDTH,
    },
    search: {
      isAvailable: false,
      isVisible: false,
    },
    menuBar: {
      boundary: 'content',
      delay: 0,
      slideDownDuration: 0,
    },
    icons: {
      TeamWorkSpeakerIconGlyph,
      DcoPersonIconGlyph,
      DcoWorldIconGlyph,
      DcoHomeIconGlyph,
      DcoDoersIconGlyph,
      DcoDoingsIconGlyph,
      DcoTownTeamsIconGlyph,
      DcoPlaceIconGlyph,
      DcoPositiveIconGlyph,
      DcoSystemShaperIconGlyph,
      DcoNewsIconGlyph,
      SearchIcon: DcoSearchIconGlyph,
      LocationIconGlyph: DcoPlaceIconGlyph,
      Space: DcoTownTeamsIconGlyph,
      DownloadAltIconGlyph: DcoDownloadIconGlyph,
    },
    explorer: {
      defaultView: 'home',
      authenticationRequired: false,
    },
    entity: {
      pageQueries: EntityPageQueries,
      dataHooks: {
        Space: () => useSpaces().filter(space => !['emerging', 'deactivated', 'hibernating'].includes(space?.status?.slug || '')),
        Video: () => useHowToVideos(),
        Post: () => useHowToPosts(),
      },
      dataProviders: [
        PagesProvider, SpacesProvider, ImpactStoriesProvider, OrganisationsProvider, PeopleProvider,
        DocumentsProvider, FeedEntriesProvider, PostsProvider, ChallengesProvider, EventsProvider, 
      ],
      cards: {
        Space: SpaceCard,
        Organisation: OrganisationCard,
        Person: PersonCard,
        Document: DocumentCard,
        ImpactStory: ImpactStoryCard,
        Link: LinkCard,

      },
      icons: {
        Space: DcoTownTeamsIconGlyph,
        SearchIcon: DcoSearchIconGlyph,
        LocationIcon: DcoPlaceIcon,
        Person: DcoPersonIconGlyph,
        Organisation: DcoOrganisationsIconGlyph,
        Document: DcoQuickWinIconGlyph,
        ImpactStory: DcoDoingsIconGlyph,
        MaturityStage: DcoPlacemakingIcon,
      },
      layouts: {
        Person: EntityExplorerLayout,
      }
    },
    entityType: {
      Video: {
        filters: {
          toggles: [
            { 
              name: 'Video', 
              label: 'How-to Videos',
            },
            
          ],
        },
        gridSort: { fieldName: 'name', desc: false },
      },
      Post: {
        filters: {
          toggles: [
            { 
              name: 'Post', 
              label: 'How-to Posts',
            },
          ],
        },
        gridSort: { fieldName: 'name', desc: false },
      },
      Document: {
        filters: {
          fieldNames: [
            { internalName: 'DocumentType', fieldName: 'type', label: 'Document Types' },
          ],
          toggles: [
            { 
              name: 'Document', 
              label: 'Quick Wins',
              filterFn: (document: Document) => document?.folder && document?.folder?.slug === 'quick-wins' 
            },
          ],
        },
        gridSort: { fieldName: 'name', desc: false },
      },
      Space: {
        gridSort: { fieldName: 'name', desc: false },
        filters: {
          fieldNames: [
            { internalName: 'Location', fieldName: 'locations', label: 'Locations' },
          ]
        },
        explorer: {
          // tabPermissions: [
          //   { 
          //     tabs: ['resources', 'conversation'],
          //     permission: (entity: Space, activePerson: ActivePersonLiveResult) => 
          //     activePerson.isAvailable && entity?.members?.some(m => m.id === activePerson?.person?.id) || activePerson?.person?.canAdmin,
          //   }
          // ],
        }
      },
      ImpactStory: {
        filters: {
          fieldNames: [
            { internalName: 'MaturityStage', fieldName: 'stage', label: 'Doing Types' },
            { internalName: 'Location', fieldName: 'location', label: 'Cities' },
            { internalName: 'State', fieldName: 'location.state', label: 'States' },
            { internalName: 'Program', fieldName: 'programs', label: 'Partnerships' },
          ],
          toggles: [
            { name: 'ImpactStory', label: 'Quick Wins', fieldName: 'quickWin' },
          ]
        },
        explorer: {
          bannerComponent: (props) => 
            <EntityBanner 
              {...props} 
              variant='image'
              showSubheader
              maxHeight={0}
              customAspectRatio={{ base: 21/9 }} 
              showOverlay 
              // entityValueProps={{ showImage: true, imageStyle: 'logo' }} 
              showColorStrip 
            />,
        }
      },
      Organisation: {
        gridSort: { fieldName: 'name', desc: false },
        filters: {
          fieldNames: [
            { internalName: 'Location', fieldName: 'location', label: 'Locations' },
          ]
        }
      },
      Person: {
        gridSort: { fieldName: 'name', desc: false },
        filters: {
          sortBy: 'name',
          toggles: [
            { name: 'Person', label: 'People' },
          ],
          fieldNames: [
            { internalName: 'Location', fieldName: 'location', label: 'Locations' },
          ]
        }
      }
    },
    componentProps: {
      SiteHeaderLayout: {
        utilityButtonGradient: { from: PRIMARY, to: PRIMARY },
        colors: {
          background: DOING_CHALK,
          text: 'light',
        }
      },
      SlideControls: {
        arrowColors: {
          fill: 'light',
          hover: {
            fill: PRIMARY,
          }
        },
        colors: {
          background: `${PRIMARY}BF`,
        }
      },
      SiteFooterLayout: {
        colors: {
          background: DOING_DEEP_BLUE,
        }
      },
      SliceOrganisationBanner: {
        showOrgBadge: false,
      },
      EntityItems: {
        showLabels: false,
      },
      ContentEntityBlock: {
        useRichTitles: true,
      },
      SiteMenuBar: {
        titleValueProps: {
          showImage: false,
        },
        colors: {
          background: DOING_CHALK,
          fill: 'strong',
        }
      },
      IndicatorGroupLayoutGrid: {
        rowWeight: 4,
        rowGapInUnits: 4,
        columnGapInUnits: 0,
        tabletRowWeight: 2,
      },
      NavigationMenuItem: {
        showIcon: false,
      },
      MenuItem: {
        colors: {
          hover: {
            background: PRIMARY,
            fill: 'light',
            text: 'light',
          },
          active: {
            fill: PRIMARY,
          }
        }
      },
      SiteHeaderUtilityButton: {
        menuThemeMode: 'dark',
        colors: {
          background: 'primary',
          text: 'strong',
          hover: {
            background: lighten(-0.07, PRIMARY),
          }
        }
      },
      MenuItemLink: {
        keepExternalLinksInTab: true,
        colors: {
          hover: {
            text: PRIMARY,
            fill: PRIMARY,
          },
          active: {
            text: PRIMARY,
            border: PRIMARY,
            fill: PRIMARY,
          }
        },
      },
      EntityValuePillList: {
        showLabel: false,
        showTitleLabel: true,
        colors: {
          background: '#FFF4F0',
          border: '#F6B8A5',
          fill: 'primary',
        }
      }
    }
  }

  return (
    <UiProvider value={UiConfig}>
      <UserMenuProvider value={{
        userIcon: DcoPersonIconGlyph,
        loginIcon: DcoLoginIconGlyph,
        logoutIcon: DcoLogoutIconGlyph,
        userMenuBottomItem: LoggedInUserMenuItems,
        loggedOutMenuItems: LoggedOutUserMenuItems,
      }}>
      <IndicatorLayoutsProvider value={{
        IndicatorLayoutLinkButton: IndicatorLayoutLinkButton,
      }}>
      <MapProvider value={mapConfig}>
      <MapDeepLinkHandler />
      <ComponentSwapProvider value={{
        LinkEntityButton: LinkEntityButton,
      }}>
      <My.Container
        className={className}
        includeTooltip
        tooltipProps={{
          backgroundColor: '#FFFFFF',
        }}
      >
        {
          isReady && <My.Map />
        }
        <My.Body>
          {children}
        </My.Body>
        {
          !isExplorer && <My.Footer />
        }
      </My.Container>
      </ComponentSwapProvider>
      </MapProvider>
      </IndicatorLayoutsProvider>
      </UserMenuProvider>
    </UiProvider>
  );
  
};

Layout.defaultProps = LayoutDefaultProps;

